import { useContext, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import { useHistory, useParams } from 'react-router-dom';

import { useQuery } from '@vyce/core/src/hooks';
import { ChatUser, Company, Shift, TimeAndAttendanceLocation } from '@vyce/core/src/types';
import { getIdFromUrlSegment } from '@vyce/core/src/utils/url';
import {
  createShiftRequest,
  deleteShiftRequest,
  getSiteRequest,
  updateShiftRequest,
  updateSiteRequest,
  getCustomLocationFields,
  updateCustomLocationFields,
} from '@vyce/core/src/api/time';
import { TimeModuleContext } from '@vyce/core/src/contexts';
import { CustomLocationField, GetStaffResponse, ListStaffRequest } from '@vyce/core/src/api/types';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { TIME_INTERFACE_PERMISSIONS } from '@vyce/core/src/types';

import { locationToSite, shiftToSiteShift, siteToLocation, prepareCustomDetailsForSending } from '../utils';
import { NotificationContext } from '../../../contexts/notificationContext';
import { useAddNewSiteManager } from './useAddNewSiteManager';

interface Props {
  selectedCompany?: Company;
  fetchCompanyData?: Function;
  chatUser?: ChatUser;
  getStaffRequest: (companyId: string, payload: ListStaffRequest) => Promise<AxiosResponse<GetStaffResponse>>;
}

export const useLocationData = ({ selectedCompany, fetchCompanyData, chatUser, getStaffRequest }: Props) => {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const [location, setLocation] = useState<TimeAndAttendanceLocation>();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [customLocationFields, setCustomLocationFields] = useState<CustomLocationField[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);

  const selectedCompanyId = selectedCompany?.uuid ?? '';
  const locationId = location?.uuid ?? '';

  const { staff, locationManagers, syncManagers, setStaffSubstring } = useAddNewSiteManager({
    selectedCompanyId,
    locationId,
    getStaffRequest,
  });
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { setBackTo } = useContext(TimeModuleContext);
  const query = useQuery();
  const { updateChat } = useChatRequests(chatUser);
  const { canAnywhere } = usePermissions();

  // Custom location fields live under the reports module and are configured in
  // Time settings, so they follow that right rather than the site's own. Without
  // it the request 403s, so it isn't sent and the fields simply don't render.
  const canReadCustomFields = canAnywhere(TIME_INTERFACE_PERMISSIONS.MANAGE_TIME_SETTINGS);

  const fetchCustomLocationFields = async (companyId: string, locationId: string) => {
    try {
      const { data } = await getCustomLocationFields({ companyId, siteId: locationId });
      setCustomLocationFields(data.values ?? []);
    } catch (e) {
      handleServerError(e);
    }
  };

  useEffect(() => {
    if (selectedCompanyId && locationId && canReadCustomFields) {
      fetchCustomLocationFields(selectedCompanyId, locationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, locationId, canReadCustomFields]);

  const getLocationData = async () => {
    const siteId = getIdFromUrlSegment(id);
    // No company or no id in the URL means there is nothing to ask for. Sending it anyway
    // put `/sites/undefined` on the wire and surfaced the gateway's "Resource ID is not
    // provided" to the user.
    if (!selectedCompanyId || !siteId) {
      return;
    }
    setLoading(true);
    try {
      const res = await getSiteRequest(selectedCompanyId, siteId);
      const loc = siteToLocation(res.data);
      setLocation(loc);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      handleServerError(e);
    }
  };

  const closeLocation = () => {
    if (!location?.uuid) {
      return;
    }
    // TODO finish when backend will be ready
    showNotification({ message: 'The location has been closed.', options: { variant: 'success' } });
    history.goBack();
  };

  const back = () => {
    history.goBack();
  };

  const saveCustomLocationChanges = async ({
    customFields,
    siteId,
  }: {
    customFields: Record<string, string>;
    siteId: string;
  }) => {
    if (!customFields || Object.keys(customFields).length === 0 || !canReadCustomFields) {
      return;
    }
    const isCustomFieldsChanged = customLocationFields.some(
      cf => (customFields[cf.system_name] ?? '') !== (cf.value ?? '')
    );
    if (!isCustomFieldsChanged) {
      return;
    }
    try {
      const preparedCustomDetailsForSending = prepareCustomDetailsForSending(customFields);
      const { data } = await updateCustomLocationFields({
        companyId: selectedCompanyId,
        siteId,
        data: { values: preparedCustomDetailsForSending },
      });
      setCustomLocationFields(data.values);
      showNotification({
        message: 'Custom Location Fields has been updated.',
        options: { variant: 'success' },
      });
    } catch (e) {
      handleServerError(e);
    }
  };

  const saveLocationChanges = async ({
    customFields,
    locationManagers,
    ...location
  }: TimeAndAttendanceLocation & {
    customFields: Record<string, string>;
    locationManagers: { uuid: string; name: string }[];
  }) => {
    if (!selectedCompanyId || !location?.uuid) {
      return;
    }
    try {
      const site = locationToSite(location);
      saveCustomLocationChanges({ customFields, siteId: location.uuid });
      await updateSiteRequest({ companyId: selectedCompanyId, siteId: location.uuid, site });
      await syncManagers(locationManagers);
      const siteShifts = location.shifts ? location.shifts.map(shift => shiftToSiteShift(shift)) : [];

      for (let i = 0; i < siteShifts.length; i++) {
        const shift = siteShifts[i];
        if (shift.uuid) {
          await updateShiftRequest({
            companyId: selectedCompanyId,
            siteId: location.uuid,
            shiftId: shift.uuid,
            shift,
          });
        }
      }
      await getLocationData();
      if (chatUser) {
        updateChat({
          companyId: selectedCompanyId,
          entityId: location.uuid,
          name: location.name,
          userId: chatUser.uid,
        });
      }
      showNotification({ message: 'Location has been updated.', options: { variant: 'success' } });
    } catch (e) {
      handleServerError(e);
    }
  };

  const createShift = async (shift: Shift) => {
    if (!selectedCompanyId || !location?.uuid) {
      return;
    }
    const siteShift = shiftToSiteShift(shift);
    try {
      await createShiftRequest({ companyId: selectedCompanyId, shift: siteShift, siteId: location.uuid });
      showNotification({ message: 'Shift has been updated.', options: { variant: 'success' } });

      getLocationData();

      if (fetchCompanyData) {
        fetchCompanyData(selectedCompanyId);
      }
    } catch (e) {
      handleServerError(e);
    }
  };

  const deleteShift = async (shiftId: string) => {
    if (!selectedCompanyId || !location?.uuid) {
      return;
    }

    try {
      await deleteShiftRequest(selectedCompanyId, location.uuid, shiftId);
      showNotification({ message: 'Shift has been deleted.', options: { variant: 'success' } });
      getLocationData();
    } catch (e) {
      handleServerError(e);
    }
  };

  useEffect(() => {
    if (!id) {
      return;
    }
    getLocationData();
    setBackTo({ text: 'Locations & Shifts', back });
    return () => {
      setBackTo(null);
    };
  }, [id]);

  useEffect(() => {
    const mode = query.get('mode');
    setEditMode(mode === 'edit');
  }, [query]);

  return {
    loading,
    workersList: staff,
    locationManagers,
    setOpenConfirmDialog,
    setEditMode,
    editMode,
    location,
    saveLocationChanges,
    openConfirmDialog,
    customLocationFields,
    closeLocation,
    createShift,
    deleteShift,
    setStaffSubstring,
  };
};
